var AbstractPlayerTech   = require("./AbstractPlayerTech"),
    VideoProgressTracker = require("app/MediaPlayer/ProgressTrackers/VideoProgressTracker"),
    get                  = require("object-path").get,
    kind                 = require("enyo/kind"),
    logger               = require("enyo/logger");

module.exports = kind(/** @lends  app.MediaPlayer.Tech.Abstract.AbstractVideoPlayer.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.Tech.Abstract.AbstractVideoPlayer",

    /**
     * @private
     */
    kind: AbstractPlayerTech,

    /**
     * @private
     */
    classes: "app-media-player-video-player",

    // Common Components
    // --------------------------------

    /**
     * The tracker kind to use.
     *
     * @type  {Function}
     * @public
     */
    trackerKind: VideoProgressTracker,

    // Internal State
    // --------------------------------

    /**
     * @type  {boolean}
     * @readonly
     * @public
     */
    isPlayingAds: false,

    /**
     * Some video formats can only start seeking once the first frame has
     * played. Use this value to determine if the player is ready to seek.
     *
     * @type    {boolean}
     * @default false
     * @private
     */
    isSeekable: false,

    /**
     * The last known cursor position within played video.
     *
     * @type  {?number}
     * @public
     */
    cursor: null,

    /**
     * The cursor position at which the player is supposed to start.
     *
     * @type  {number}
     * @public
     */
    startCursor: 0,

    /**
     * This value indicates whether video playback has reached the startCursor.
     *
     * @type     {boolean}
     * @default  false
     * @public
     */
    hasStarted: false,

    /**
     * @private
     */
    isPlayingAdsChanged: function() {
        this.app.$.state.set("mediaPlayerPlayingAds", this.get("isPlayingAds"));
    },

    /**
     * Resets player to the state of an uninitialized player.
     *
     * @private
     */
    resetPlayerState: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.set("cursor", null);
            this.set("hasStarted", false);
            this.set("isSeekable", false);
        };
    }),

    // VideoPlayer Events
    // --------------------------------

    /**
     * @public
     */
    events: {
        onPlayerError: ""
    },

    // VideoPlayer API
    // --------------------------------

    /**
     * The following methods *must* be implemented in order for the player to be
     * compatible with the rest of the application.
     */

    /**
     * Hide the controls (this may not be possible for every player).
     *
     * @returns  {boolean}  `true` if hidden, `false` otherwise.
     * @public
     */
    hideControls: function() {
        return false;
    },

    /**
     * Show the controls (this may not be possible for every player).
     *
     * @returns  {boolean}  `true` if showing, `false` otherwise.
     * @public
     */
    showControls: function() {
        return false;
    },

    /**
     * Start playing
     *
     * @public
     */
    play: function() {
        var errorMsg = "app.MediaPlayer.Tech.Abstract.AbstractVideoPlayer.play() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Move to a certain cursor position
     *
     * @param  {number}  cursor  The cursor position to seek to
     *
     * @public
     */
    seek: function() {
        var errorMsg = "app.MediaPlayer.Tech.Abstract.AbstractVideoPlayer.seekToAndPlay(cursor) not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Pause the player
     *
     * @public
     */
    pause: function() {
        var errorMsg = "app.MediaPlayer.Tech.Abstract.AbstractVideoPlayer.pause() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Stop the player
     *
     * @public
     */
    stop: function() {
        var errorMsg = "app.MediaPlayer.Tech.Abstract.AbstractVideoPlayer.stop() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Return the current point on the player's timeline in seconds.
     *
     * @returns  {Promise<number|null>}  The cursor if available, `null` otherwise.
     * @public
     */
    getCursor: function() {
        var errorMsg = "app.MediaPlayer.Tech.Abstract.AbstractVideoPlayer.getCursor() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }

        return new Promise(function(resolve) {
            resolve(null);
        });
    },

    /**
     * Return the duration in seconds of the current content.
     *
     * @returns  {Promise<number|null>}  The duration if available, `null` otherwise.
     * @public
     */
    getLength: function() {
        var errorMsg = "app.MediaPlayer.Tech.Astract.AbstractVideoPlayer.getLength() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }

        return new Promise(function(resolve) {
            resolve(null);
        });
    },

    /**
     * Skip to next track.
     *
     * @public
     */
    nextTrack: function() {
        var state     = this.state || {},
            nextMedia = get(state, "nextMedia", null);

        nextMedia && this.app.$.mediaPlayerManager.autoNavToMediaItem(nextMedia);
    },

    /**
     * Skip to previous track.
     *
     * @public
     */
    previousTrack: function() {
        var state     = this.state || {},
            prevMedia = get(state, "prevMedia", null);

        prevMedia && this.app.$.mediaPlayerManager.autoNavToMediaItem(prevMedia);
    }

});
